import { itemDef, STATION_INPUTS, STATION_ITEMS, STATION_NAMES, stationRecipes, type ProcessStation } from "@eldrun/content";
import { isDiscovered } from "@eldrun/sim";

/** O que o cliente lê do schema `StationState`. */
export interface StationLike {
  kind: string;
  burn: number;
  burnMax: number;
  active: boolean;
  recipe: string;
  progress: number;
  duration: number;
  rate: number;
}

/**
 * Barras da estação entre os eventos do servidor: o schema só muda ao acender, terminar ou parar;
 * entre eles, o progresso anda no ritmo do fogo e a brasa queima 1 s por segundo enquanto ativa.
 */
export class StationClock {
  private key = "";
  private at = 0;

  /** `id` da estação: abrir outra (ou reabrir) reancora mesmo que os valores coincidam. */
  bars(st: StationLike, now: number, id = ""): { progress: number; fire: number } {
    const key = `${id}|${st.active}|${st.recipe}|${st.progress}|${st.burn}|${st.rate}`;
    if (key !== this.key) {
      this.key = key;
      this.at = now;
    }
    const dt = st.active ? (now - this.at) / 1000 : 0;
    const progress = st.duration > 0 ? Math.min(1, (st.progress + dt * st.rate) / st.duration) : 0;
    const fire = st.burnMax > 0 ? Math.max(0, st.burn - dt) / st.burnMax : 0;
    return { progress, fire };
  }
}

export interface StationRecipeLine {
  id: string;
  text: string;
  current: boolean;
}

const name = (id: string) => itemDef(id)?.name ?? id;

/** Livro de receitas da estação (só as descobertas, doc 03), com a que está em andamento marcada. */
export function stationBook(kind: ProcessStation, known: (itemId: string) => boolean, current: string): StationRecipeLine[] {
  return stationRecipes(kind)
    .filter((r) => isDiscovered(r, known, STATION_ITEMS[kind]))
    .map((r) => ({
      id: r.id,
      current: r.id === current,
      text: `${r.qty > 1 ? `${r.qty} ` : ""}${name(r.output)} ← ${r.inputs.map((i) => `${i.qty} ${name(i.item)}`).join(" + ")} · ${r.seconds ?? 0} s${r.coalOnly ? " (só Carvão)" : ""}`,
    }));
}

/** Rótulos dos slots da estação: fogo, entradas e saída. */
export function stationLabels(kind: ProcessStation): string[] {
  const n = STATION_INPUTS[kind];
  const labels = ["Fogo"];
  for (let i = 0; i < n; i++) labels.push(n > 1 ? `Ingrediente ${i + 1}` : "Entrada");
  labels.push("Saída");
  return labels;
}

export const stationTitle = (kind: ProcessStation): string => STATION_NAMES[kind];
